import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  X,
} from 'lucide-react';
import { GameStateGrant, GsGrantReviewStatus } from '@/lib/api';
import { useAdminGrants, useApproveGrant, useRejectGrant } from '../hooks';

const PAGE_SIZE = 25;

const STATUS_TABS: { label: string; value: GsGrantReviewStatus | undefined }[] = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'All', value: undefined },
];

function statusBadgeVariant(
  status: GsGrantReviewStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'approved') return 'default';
  if (status === 'rejected') return 'destructive';
  return 'secondary';
}

interface RejectDialogState {
  grant: GameStateGrant;
  reason: string;
}

export function AdminGrantsPage() {
  const navigate = useNavigate();
  const [activeStatus, setActiveStatus] = useState<GsGrantReviewStatus | undefined>('pending');
  const [page, setPage] = useState(0);
  const [archeFilter, setArcheFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState | null>(null);

  const { grants, total, loading, error, refresh } = useAdminGrants({
    reviewStatus: activeStatus,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const approveMutation = useApproveGrant();
  const rejectMutation = useRejectGrant();

  // Client-side substring filter for arche-id and resource-name. Avoids
  // adding new server-side query params just for fuzzy admin browsing.
  const visibleGrants = grants.filter((g) => {
    if (archeFilter && !g.archeId.toLowerCase().includes(archeFilter.toLowerCase())) {
      return false;
    }
    if (resourceFilter && !g.resourceName.toLowerCase().includes(resourceFilter.toLowerCase())) {
      return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleApprove = (g: GameStateGrant) => {
    approveMutation.mutate({
      archeId: g.archeId,
      resourceType: g.resourceType,
      resourceName: g.resourceName,
    });
  };

  const handleConfirmReject = () => {
    if (!rejectDialog) return;
    rejectMutation.mutate(
      {
        archeId: rejectDialog.grant.archeId,
        resourceType: rejectDialog.grant.resourceType,
        resourceName: rejectDialog.grant.resourceName,
        reason: rejectDialog.reason.trim() || undefined,
      },
      {
        onSettled: () => setRejectDialog(null),
      },
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_dfe_game_state/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <h1 className="text-2xl font-semibold">Plugin Grants</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Approve plugin-arche access to per-user keywords and tasks. Pending grants are refused at
          runtime until approved. A pending row appears automatically the first time a plugin
          attempts to access a keyword it has not been granted; the requestor column shows the end
          user whose invocation triggered the request.
        </p>
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            size="sm"
            variant={activeStatus === tab.value ? 'default' : 'outline'}
            onClick={() => {
              setActiveStatus(tab.value);
              setPage(0);
            }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground block mb-1">Arche ID</label>
              <input
                type="text"
                value={archeFilter}
                onChange={(e) => setArcheFilter(e.target.value)}
                placeholder="Filter by arche id substring..."
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground block mb-1">Resource name</label>
              <input
                type="text"
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
                placeholder="Filter by keyword name..."
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={refresh}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="py-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      ) : visibleGrants.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <KeyRound className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No grants found</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {activeStatus
                ? `No ${activeStatus} grants match your filters.`
                : 'No grants match your filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium">Arche</th>
                <th className="text-left p-3 font-medium">Type</th>
                <th className="text-left p-3 font-medium">Resource</th>
                <th className="text-left p-3 font-medium">Access</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Requestor</th>
                <th className="text-left p-3 font-medium">Grantor</th>
                <th className="text-left p-3 font-medium">Updated</th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleGrants.map((g) => (
                <tr
                  key={`${g.archeId}:${g.resourceType}:${g.resourceName}`}
                  className="border-b last:border-b-0 hover:bg-muted/30"
                >
                  <td className="p-3">
                    <Link
                      to={`/a/arche_dfe_game_state/admin/grants/${encodeURIComponent(g.archeId)}/${encodeURIComponent(g.resourceType)}/${encodeURIComponent(g.resourceName)}`}
                      className="hover:underline"
                    >
                      <div>
                        {g.archeName ?? (
                          <span className="text-muted-foreground italic">unknown</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{g.archeId}</div>
                    </Link>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline">{g.resourceType}</Badge>
                  </td>
                  <td className="p-3 font-mono text-xs">{g.resourceName}</td>
                  <td className="p-3 font-mono text-xs">{g.access}</td>
                  <td className="p-3">
                    <Badge variant={statusBadgeVariant(g.reviewStatus)}>{g.reviewStatus}</Badge>
                  </td>
                  <td className="p-3 text-xs">
                    {g.createdByUsername ?? (
                      <span className="font-mono text-muted-foreground">{g.createdBy}</span>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {g.reviewedBy == null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      (g.reviewedByUsername ?? (
                        <span className="font-mono text-muted-foreground">{g.reviewedBy}</span>
                      ))
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {new Date(g.updatedAt).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      {g.reviewStatus !== 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleApprove(g)}
                          disabled={approveMutation.isPending}
                        >
                          <Check className="h-3 w-3 mr-1" /> Approve
                        </Button>
                      )}
                      {g.reviewStatus !== 'rejected' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRejectDialog({ grant: g, reason: '' })}
                          disabled={rejectMutation.isPending}
                        >
                          <X className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          navigate(
                            `/a/arche_dfe_game_state/admin/grants/${encodeURIComponent(g.archeId)}/${encodeURIComponent(g.resourceType)}/${encodeURIComponent(g.resourceName)}`,
                          )
                        }
                      >
                        Details
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages} ({total} total)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {rejectDialog && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 space-y-4">
              <h2 className="text-lg font-semibold">Reject grant</h2>
              <p className="text-sm text-muted-foreground">
                Reject access for{' '}
                <span>{rejectDialog.grant.archeName ?? rejectDialog.grant.archeId}</span> to{' '}
                <span className="font-mono">{rejectDialog.grant.resourceName}</span>?
              </p>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Reason (optional, max 1000 chars)
                </label>
                <textarea
                  value={rejectDialog.reason}
                  onChange={(e) =>
                    setRejectDialog({ ...rejectDialog, reason: e.target.value.slice(0, 1000) })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                  placeholder="e.g. scope too broad, requires security review"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRejectDialog(null)}
                  disabled={rejectMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleConfirmReject}
                  disabled={rejectMutation.isPending}
                >
                  {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
